import { 
    Component, 
    ElementRef, 
    viewChild, 
    input, 
    signal, 
    computed, 
    effect, 
    forwardRef, 
    ChangeDetectionStrategy, 
    output
  } from '@angular/core';
  import { CommonModule } from '@angular/common';
  import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
  
  @Component({
    selector: 'ops-numeric-input',
    standalone: true,
    imports: [CommonModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
      {
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => NumericInputComponent),
        multi: true
      }
    ],
    templateUrl: './numeric-input.component.html',
    styleUrls: ['./numeric-input.component.scss'],
  })
  export class NumericInputComponent implements ControlValueAccessor {
    // --- Inputs ---
    decimal = input<number>(2);
    disabled = input<boolean>(false);
    value = input<number | null>(null);
    align = input<'left' | 'center' | 'right'>('right');
    isValueHidden = input<boolean>(false);
  
    // --- State Signals ---
    valueSignal = signal<number | null>(null);
    isFocused = signal(false);
    isDisabled = signal(false);
    internalValue = signal<string>(''); // Current string in the input
    private snapshotValue = signal<string>(''); // For Escape-key revert
    private isUpdatingFromUser = false; // Flag to prevent overwriting user input
    private hasBeenFocused = false; // Track if input has been focused before
    
    // --- Element Refs ---
    inputElement = viewChild<ElementRef<HTMLInputElement>>('inputRef');
  
    // --- CVA Callbacks ---
    private onChange: (value: number | null) => void = () => {};
    private onTouched: () => void = () => {};
  
    // --- Computed States ---
    
    // Idle state: formatted with commas, no trailing zeros
    formattedValue = computed(() => {
      const val = this.valueSignal();
      if (val === null || val === undefined || this.isValueHidden()) return '';
      
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: this.decimal(),
      }).format(val);
    });
  
    // Edit state: raw string currently being typed
    displayValue = computed(() => this.internalValue());

    // Add this output to the component class
    onValueChange = output<number | null>();
  
    constructor() {
      // Sync disabled input to disabled signal
      effect(() => {
        this.isDisabled.set(this.disabled());
      }, { allowSignalWrites: true });

      // Sync value input to valueSignal (only when not being edited by user)
      effect(() => {
        const inputVal = this.value();
        // Don't overwrite if user is currently editing or just updated from user input
        if (this.isFocused() || this.isUpdatingFromUser) return;
        if (inputVal !== this.valueSignal()) {
          this.writeValue(inputVal);
        }
      }, { allowSignalWrites: true });

      // Optimized Debounce: Syncs internal string to the form model after 500ms
      effect((onCleanup) => {
        const val = this.internalValue();
        
        // Skip emitting if it's the initial load or empty
        if (val === '' && this.valueSignal() === null) return;
  
        const timeout = setTimeout(() => {
          let rawNumeric = parseFloat(val.replace(/,/g, ''));
          const finalValue = isNaN(rawNumeric) ? null : rawNumeric;
          
          // Round to specified decimal places
          if (finalValue !== null && this.decimal() >= 0) {
            const multiplier = Math.pow(10, this.decimal());
            rawNumeric = Math.round(finalValue * multiplier) / multiplier;
          }
  
          if (this.valueSignal() !== rawNumeric) {
            this.isUpdatingFromUser = true;
            this.valueSignal.set(rawNumeric);
            this.onChange(rawNumeric);
            
            // --- Emit the new value to the parent ---
            this.onValueChange.emit(rawNumeric);
            
            // Reset flag after a delay to allow parent to update
            // Check if parent value matches what we set before resetting
            setTimeout(() => {
              if (this.value() === rawNumeric) {
                this.isUpdatingFromUser = false;
              } else {
                // Parent hasn't updated yet, wait a bit more
                setTimeout(() => {
                  this.isUpdatingFromUser = false;
                }, 300);
              }
            }, 200);
          }
        }, 500);
  
        onCleanup(() => clearTimeout(timeout));
      }, { allowSignalWrites: true });
    }
  
    // --- ControlValueAccessor Implementation ---
  
    writeValue(obj: any): void {
      const numericValue = typeof obj === 'number' ? obj : null;
      this.valueSignal.set(numericValue);
      
      // Sync internal string for the input field
      if (numericValue !== null) {
        this.internalValue.set(numericValue.toString());
      } else {
        this.internalValue.set('');
      }
    }
  
    registerOnChange(fn: any): void { this.onChange = fn; }
    registerOnTouched(fn: any): void { this.onTouched = fn; }
    setDisabledState(isDisabled: boolean): void { this.isDisabled.set(isDisabled); }
  
    // --- Interaction Logic ---
  
    setFocus() {
      if (this.isDisabled()) return;
      
      // Snapshot current state to allow Escape key revert
      this.snapshotValue.set(this.internalValue());
      
      // If already focused, don't do anything (let user position cursor normally)
      if (this.isFocused()) {
        return;
      }
      
      this.isFocused.set(true);
      
      // Microtask to ensure input is rendered before focus
      setTimeout(() => {
        const el = this.inputElement()?.nativeElement;
        if (el) {
          el.focus();
        }
      });
    }
    
    onInputFocus() {
      // Handle focus event from the input element - select only on first focus
      if (!this.hasBeenFocused) {
        setTimeout(() => {
          const el = this.inputElement()?.nativeElement;
          if (el) {
            el.select();
            this.hasBeenFocused = true;
          }
        });
      }
    }
  
    onContainerFocus(event: FocusEvent) {
      // Handle focus from tab key or programmatic focus
      if (!this.isDisabled()) {
        this.setFocus();
      }
    }

    onContainerBlur(event: FocusEvent) {
      // Only blur if focus is not moving to the input element
      const relatedTarget = event.relatedTarget as HTMLElement;
      const inputEl = this.inputElement()?.nativeElement;
      
      // If focus is moving to the input, don't blur the container
      if (relatedTarget === inputEl || inputEl?.contains(relatedTarget)) {
        return;
      }
      
      // Focus is leaving the component
      this.isFocused.set(false);
      this.onTouched();
      this.hasBeenFocused = false;
    }

    onBlur() {
      // Check if focus is moving to the container
      const activeElement = document.activeElement as HTMLElement;
      const containerEl = activeElement?.closest('.numeric-container') as HTMLElement;
      
      // If focus is staying within the component, don't blur
      if (containerEl) {
        return;
      }
      
      this.isFocused.set(false);
      this.onTouched();
      // Reset focus flag when blurring so next focus will select again
      this.hasBeenFocused = false;
    }
  
    handleInput(event: Event) {
      const target = event.target as HTMLInputElement;
      const oldValue = target.value;
      const caretPosition = target.selectionStart || 0;
      
      let rawValue = target.value.replace(/[^0-9.]/g, '');
      
      // If decimal places is 0, remove decimal point
      if (this.decimal() === 0) {
        rawValue = rawValue.replace(/\./g, '');
      }
      
      // Prevent multiple decimal points
      const parts = rawValue.split('.');
      if (parts.length > 2) {
        rawValue = parts[0] + '.' + parts.slice(1).join('');
      }

      // Restrict decimal places
      if (parts.length === 2 && parts[1].length > this.decimal()) {
        rawValue = parts[0] + '.' + parts[1].substring(0, this.decimal());
      }
  
      // Format with commas
      const formattedValue = this.formatWithCommas(rawValue);
      this.internalValue.set(formattedValue);
      
      // Restore caret position
      requestAnimationFrame(() => {
        const newCaretPosition = this.calculateNewCaretPosition(
          oldValue,
          formattedValue,
          caretPosition
        );
        target.setSelectionRange(newCaretPosition, newCaretPosition);
      });
    }
  
    handleKeyDown(event: KeyboardEvent) {
      const functionalKeys = [
        'Backspace', 'Tab', 'Enter', 'Escape', 'ArrowLeft', 'ArrowRight', 
        'Delete', 'Home', 'End', 'Control', 'Alt', 'Shift', 'Meta'
      ];

      // Restrict to Numbers and Dot only (if decimals allowed)
      if (!/^[0-9.]$/.test(event.key) && !functionalKeys.includes(event.key)) {
        event.preventDefault();
        return;
      }
      
      if (functionalKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
        
        // ESCAPE Logic: Revert and exit
        if (event.key === 'Escape') {
          event.preventDefault();
          this.internalValue.set(this.snapshotValue());
          this.isFocused.set(false);
          return;
        }
  
        // ENTER Logic: Commit and exit
        if (event.key === 'Enter') {
          event.preventDefault();
          (event.target as HTMLInputElement).blur();
        }
        return;
      }
  
      // Restrict to Numbers and Dot only (if decimals allowed)
      if (this.decimal() === 0 && event.key === '.') {
        event.preventDefault();
        return;
      }

      // Restrict to specified decimal places
      const splitValue = this.internalValue().split('.');
      if (this.decimal() > 0 && splitValue[1] && splitValue[1].length >= this.decimal()) {
        const target = event.target as HTMLInputElement;
        const caretPosition = target.selectionStart || 0;
        if(caretPosition > splitValue[0].length) {
          event.preventDefault();
          return;
        }
        return;
      }
    }
  
    private formatWithCommas(val: string): string {
      if (!val) return '';
      const parts = val.split('.');
      // Add commas to the integer part
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }

    /**
     * Calculate the new caret position after formatting changes
     * Accounts for commas being added/removed and decimal point position
     */
    private calculateNewCaretPosition(
      oldValue: string,
      newValue: string,
      oldCaretPosition: number
    ): number {
      if (oldCaretPosition === 0) return 0;
      if (oldCaretPosition >= oldValue.length) return newValue.length;
      
      // Count digits and decimal point before caret in old value
      let digitsBeforeCaret = 0;
      let decimalPointBeforeCaret = false;
      
      for (let i = 0; i < oldCaretPosition && i < oldValue.length; i++) {
        const char = oldValue[i];
        if (/[0-9]/.test(char)) {
          digitsBeforeCaret++;
        } else if (char === '.') {
          decimalPointBeforeCaret = true;
        }
      }
      
      // Find position in new value
      let newPosition = 0;
      let digitsCounted = 0;
      let foundDecimalPoint = false;
      
      for (let i = 0; i < newValue.length; i++) {
        const char = newValue[i];
        
        if (/[0-9]/.test(char)) {
          digitsCounted++;
          // If we've counted enough digits, position after this digit
          if (digitsCounted === digitsBeforeCaret) {
            // Check if we need to account for decimal point
            if (decimalPointBeforeCaret && !foundDecimalPoint && i < newValue.length - 1 && newValue[i + 1] === '.') {
              newPosition = i + 2; // Position after decimal point
            } else {
              newPosition = i + 1; // Position after this digit
            }
            break;
          }
        } else if (char === '.') {
          foundDecimalPoint = true;
          // If caret was right before decimal point in old value
          if (digitsCounted === digitsBeforeCaret && !decimalPointBeforeCaret) {
            newPosition = i;
            break;
          }
        } else if (char === ',') {
          // Commas don't affect digit count, but we track position
          if (digitsCounted < digitsBeforeCaret) {
            newPosition = i + 1;
          }
        }
      }
      
      // If we didn't find enough digits, position at end
      if (digitsCounted < digitsBeforeCaret) {
        newPosition = newValue.length;
      }
      
      return Math.min(Math.max(newPosition, 0), newValue.length);
    }
  }